import React, { useEffect, useState } from 'react';
import { usePerformanceMetrics } from '../../hooks/usePerformanceTracking';
import { AlertData } from '../../utils/performanceMonitor';

interface PerformanceMetrics {
  renders: Record<string, number>;
  apiCalls: Record<string, number>;
  memorySnapshots: number[];
  currentMemory: number | null;
}

const PerformanceDebugger: React.FC = () => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    renders: {},
    apiCalls: {},
    memorySnapshots: [],
    currentMemory: null,
  });
  const [alerts, setAlerts] = useState<Array<{ type: string; data: AlertData; timestamp: number }>>([]);
  const [isVisible, setIsVisible] = useState(false);
  
  const { getMetrics, onAlert } = usePerformanceMetrics();
  
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    
    // Update metrics every 5 seconds to reduce flickering
    const interval = setInterval(() => {
      setMetrics(getMetrics());
    }, 5000);
    
    // Subscribe to alerts
    const unsubscribe = onAlert((type: string, data: AlertData) => {
      setAlerts(prev => [
        ...prev.slice(-9), // Keep only last 10 alerts
        { type, data, timestamp: Date.now() }
      ]);
    });
    
    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, [getMetrics, onAlert]);
  
  if (process.env.NODE_ENV !== 'development') return null;
  
  const formatMemory = (bytes: number | null) => {
    if (!bytes) return 'N/A';
    return `${(bytes / 1024 / 1024).toFixed(2)}MB`;
  };
  
  const getAlertColor = (type: string) => {
    switch (type) {
      case 'excessive-renders': return '#ff6b6b';
      case 'excessive-api-calls': return '#ffa726';
      case 'high-memory-usage': return '#ef5350';
      default: return '#42a5f5';
    }
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: 20,
      right: 20,
      background: 'rgba(0, 0, 0, 0.9)',
      color: 'white',
      padding: '10px',
      borderRadius: '8px',
      fontSize: '12px',
      maxWidth: '400px',
      maxHeight: '500px',
      overflow: 'auto',
      zIndex: 10000,
      fontFamily: 'monospace',
      border: '1px solid #333',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
        borderBottom: '1px solid #333',
        paddingBottom: '8px',
      }}>
        <h4 style={{ margin: 0, color: '#4fc3f7' }}>Performance Monitor</h4>
        <button
          onClick={() => setIsVisible(!isVisible)}
          style={{
            background: 'transparent',
            border: '1px solid #666',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '10px',
          }}
        >
          {isVisible ? 'Hide' : 'Show'}
        </button>
      </div>
      
      {isVisible && (
        <>
          {/* Memory Usage */}
          <div style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#81c784' }}>Memory Usage:</strong>
            <div style={{ marginLeft: '8px', fontSize: '11px' }}>
              Current: {formatMemory(metrics.currentMemory)}
            </div>
            {metrics.memorySnapshots.length > 0 && (
              <div style={{ marginLeft: '8px', fontSize: '11px' }}>
                Avg (last 10): {formatMemory(
                  metrics.memorySnapshots.reduce((a, b) => a + b, 0) / metrics.memorySnapshots.length
                )}
              </div>
            )}
          </div>

          {/* Recent Alerts */}
          {alerts.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <strong style={{ color: '#ff8a65' }}>Recent Alerts:</strong>
              <div style={{ maxHeight: '100px', overflow: 'auto', marginTop: '4px' }}>
                {alerts.slice(-5).reverse().map((alert, index) => (
                  <div
                    key={index}
                    style={{
                      fontSize: '10px',
                      padding: '2px 4px',
                      margin: '2px 0',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderLeft: `3px solid ${getAlertColor(alert.type)}`,
                      borderRadius: '2px',
                    }}
                  >
                    <div style={{ color: getAlertColor(alert.type), fontWeight: 'bold' }}>
                      {alert.type.replace('-', ' ').toUpperCase()}
                    </div>
                    <div>
                      {alert.data.componentName && `Component: ${alert.data.componentName}`}
                      {alert.data.endpoint && `Endpoint: ${alert.data.endpoint}`}
                      {` (${alert.data.count})`}
                    </div>
                    <div style={{ color: '#999', fontSize: '9px' }}>
                      {new Date(alert.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Render Counts */}
          <details style={{ marginBottom: '8px' }}>
            <summary style={{ cursor: 'pointer', color: '#ffb74d' }}>
              Render Counts ({Object.keys(metrics.renders).length})
            </summary>
            <div style={{ marginLeft: '12px', marginTop: '4px', maxHeight: '120px', overflow: 'auto' }}>
              {Object.entries(metrics.renders).length === 0 ? (
                <div style={{ color: '#999', fontSize: '10px' }}>No recent renders</div>
              ) : (
                Object.entries(metrics.renders)
                  .sort(([,a], [,b]) => b - a)
                  .slice(0, 10)
                  .map(([key, count]) => (
                    <div key={key} style={{ fontSize: '10px', margin: '2px 0' }}>
                      <span style={{ color: count > 5 ? '#ff6b6b' : '#81c784' }}>
                        {key}: {count}
                      </span>
                    </div>
                  ))
              )}
            </div>
          </details>

          {/* API Calls */}
          <details style={{ marginBottom: '8px' }}>
            <summary style={{ cursor: 'pointer', color: '#64b5f6' }}>
              API Calls ({Object.keys(metrics.apiCalls).length})
            </summary>
            <div style={{ marginLeft: '12px', marginTop: '4px', maxHeight: '120px', overflow: 'auto' }}>
              {Object.entries(metrics.apiCalls).length === 0 ? (
                <div style={{ color: '#999', fontSize: '10px' }}>No recent API calls</div>
              ) : (
                Object.entries(metrics.apiCalls)
                  .sort(([,a], [,b]) => b - a)
                  .slice(0, 10)
                  .map(([key, count]) => (
                    <div key={key} style={{ fontSize: '10px', margin: '2px 0' }}>
                      <span style={{ color: count > 3 ? '#ffa726' : '#81c784' }}>
                        {key}: {count}
                      </span>
                    </div>
                  ))
              )}
            </div>
          </details>

          {/* Controls */}
          <div style={{
            borderTop: '1px solid #333',
            paddingTop: '8px',
            marginTop: '8px',
            display: 'flex',
            gap: '8px',
          }}>
            <button
              onClick={() => {
                setMetrics({ renders: {}, apiCalls: {}, memorySnapshots: [], currentMemory: null });
                setAlerts([]);
              }}
              style={{
                background: '#d32f2f',
                border: 'none',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '10px',
              }}
            >
              Clear
            </button>
            <button
              onClick={() => console.log('Performance Metrics:', getMetrics())}
              style={{
                background: '#1976d2',
                border: 'none',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '10px',
              }}
            >
              Log to Console
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PerformanceDebugger; 